/**
 * ATL-1445 stage 3, P5 — the app's binding of the write-plane watch and of the startup outbox look.
 *
 * `writeCutoverWatch.ts` holds the rules and the sequences, all injectable; `sessionOutboxV2.ts` holds
 * the session drainer. This file binds them to the app's real dependencies (toasts, the reload, the
 * memory, the pending-skip queue, the in-flight counter, telemetry) so `AppStack.js` passes only the UI
 * primitives it owns. Nothing here runs for an organization whose writes are Firestore-master and
 * that has nothing in outbox v2 — every organization today.
 */
import * as Sentry from '@sentry/react-native'

import { LIVE_ROLLBACK_DRAIN_CAP_MS, LIVE_ROLLBACK_GRACE_MS, type OrgWritePlaneWatch } from '../Firebase/orgReadCutoverWatch'
import { drainPendingSkipsBeforeRestart } from '../Firebase/dualWrite'
import { waitForWritesToDrain } from '../API/writesInFlight'
import { captureOperationalEvent } from '../analytics/operationalTelemetry'
import { createOutboxAlertSink } from './outboxAlerts'
import {
  ensureSessionOutboxV2,
  getSessionOutboxV2,
  productionSessionOutboxV2Deps,
  sessionOutboxV2SendExclusive,
  takeProbedOutboxListing,
  triggerActiveSessionOutboxV2,
  type SessionOutboxV2Deps,
} from './sessionOutboxV2'
import { activateLocalView, settleLocalViewStartupBarrier } from './localView'
import { beginV1Migration } from './v1MigrationBarrier'
import type { V1MigrationDeps, V1MigrationResult } from './v1Migration'
import {
  ROW3_DRAIN_CAP_MS,
  createWritePlaneWatch,
  performV2DrainThenRestart,
  performWriteCutover,
  type WriteCutoverDecision,
} from './writeCutoverWatch'
import { getSessionWriteModePin, installWriteWBackendFirst, installWriteWNotices, notifyWriteW, type WriteWNotice } from './writeW'
import { createBackendFirstBranch, type BackendFirstDeps } from './writeWBackendFirst'
import { WRITE_W_CAPABILITY } from './writeWCapability'
import type { OutboxV2Presence } from './writeMode'
import { writesMasterMemory } from './writesMasterMemory'

export type AppToastType = 'warning' | 'danger' | 'normal'

export interface AppWritePlaneParams {
  organizationId: string
  showToast: (message: string, options: { type: AppToastType; duration: number }) => void
  reload: () => void
  /** The watcher's `reevaluateWritePlane`, for the drainer's `master-changed` re-read. */
  onMasterRecheck?: () => void
  /** PostHog super-properties for the outbox sink. */
  register?: (props: Record<string, number | null>) => unknown
  /**
   * Where the write plane logs (default `console`). The watcher's own logger is passed here too, so
   * every line either plane writes is in the same stream — and in the differential's traces.
   */
  logger?: Pick<Console, 'error' | 'warn' | 'log'>
}

/** User-facing copy. Russian, like the rest of the till's notices. */
export const WRITE_CUTOVER_TEXT = {
  writeCutover: 'Организация переключена на новый способ записи. Приложение перезапустится через несколько секунд.',
  syncing:
    'Синхронизация операций с сервером… Работу можно продолжать; приложение перезапустится, когда всё будет отправлено.',
  /**
   * Why the drain stopped, one text per reason (review 2, item 4): each says what is actually wrong.
   * `untilResolved` is appended only to a drain that will NOT restart without it (one that lowers the
   * memory); a capped read rollback restarts anyway, so it must not promise otherwise.
   */
  blocked: {
    held: 'Синхронизация остановлена: сервер не принял операцию — обратитесь в поддержку.',
    'unreadable-record': 'Синхронизация остановлена: на устройстве повреждена запись неотправленной операции — обратитесь в поддержку.',
    'foreign-install': 'Синхронизация остановлена: на устройстве есть операции, сохранённые на другом устройстве — обратитесь в поддержку.',
    'outbox-unavailable': 'Не удаётся прочитать неотправленные операции на этом устройстве — обратитесь в поддержку.',
  },
  untilResolved: ' Приложение не будет перезапущено, пока это не решено.',
  restartAfterSync: 'Синхронизация завершена. Приложение перезапустится через несколько секунд.',
  restartUndrained:
    'Источник данных переключён. Приложение перезапустится через несколько секунд; неотправленные операции будут отправлены после перезапуска.',
} as const

/** The duration of the persistent notices (the toast library has no "until dismissed"). */
export const WRITE_CUTOVER_NOTICE_MS = 30_000

/**
 * P9 — what the till shows when the seam refuses or holds back a W write (`WriteWNotice`). S2 (§11):
 * "a till that silently refuses every sale is not acceptable", so every refusal reason says what is
 * wrong and what to do. Russian, like every other notice of the till.
 */
export const WRITE_W_NOTICE_TEXT = {
  refused: {
    // Supabase-master organization, this bundle cannot honour it (decision S1; enforced by ATL-1601).
    'bundle-incapable': 'Эта версия приложения не может сохранять операции вашей организации. Обновите приложение.',
    // §4's invalid state — on web, every offline cold start of a cut-over organization (S2).
    'writes-supabase-without-reads':
      'Данные организации не загружены с сервера, поэтому операции сейчас не сохраняются. Проверьте подключение к интернету и перезапустите приложение.',
    'outbox-v2-not-drained':
      'Сначала нужно отправить на сервер ранее сохранённые операции. Проверьте подключение к интернету.',
    unpinned: 'Приложение ещё загружает настройки организации. Повторите через несколько секунд.',
    // Not refusal reasons (a session in these is in Firestore or Supabase mode); kept total for the type.
    'org-firestore-master': 'Операция не сохранена. Повторите попытку.',
    'org-supabase-master': 'Операция не сохранена. Повторите попытку.',
    'org-supabase-master-preview': 'Операция не сохранена. Повторите попытку.',
    'outbox-v2-draining': 'Операция не сохранена. Повторите попытку.',
  },
  // P9b, staging cut-over preview only: an unconverted W writer in Supabase mode (`assertWWriterAvailable`).
  previewUnavailable:
    'Недоступно в предпросмотре перехода: эта операция ещё не переведена на новый сервер (тестовая среда). Ничего не сохранено.',
  syncing: 'Синхронизация операций с сервером… Операция будет сохранена после её завершения.',
  notSent: {
    'queue-not-empty':
      'Операция недоступна, пока не отправлены ранее сохранённые операции. Проверьте подключение к интернету и повторите.',
    'other-tab': 'Операции отправляются из другой вкладки. Повторите через несколько секунд.',
    migrating: 'Идёт синхронизация операций с сервером. Повторите через минуту.',
    paused: 'Идёт проверка настроек организации. Повторите через минуту.',
    stopped: 'Операция не выполнена: сеанс завершён. Войдите снова.',
    storage: 'Не удаётся прочитать неотправленные операции на этом устройстве — обратитесь в поддержку.',
    'no-drainer': 'Операция не выполнена: отправка операций не запущена. Перезапустите приложение.',
    // Review 1, S2-a: a HELD (or unreadable, or foreign) earlier write stops the queue; waiting or a
    // better connection cannot help, so this must not read as a connectivity problem.
    'queue-held':
      'Синхронизация остановлена: одна из ранее сохранённых операций требует внимания (сервер её не принял). Пока это не решено, операция недоступна — обратитесь к администратору или в поддержку.',
  },
  // Review 1, S2-b: a refuse-offline write was sent and may have applied; its tail did not run.
  outcomeUnknown: {
    'sales.delete': {
      // Review 2, S3-5: the server answered, but not with an answer that says THIS action voided it.
      unrecognized:
        'Сервер ответил на удаление продажи непредвиденно: приложение не может подтвердить, удалило ли продажу это действие. Откройте продажу и проверьте её, прежде чем удалять снова: чек возврата в ОФД не выбит автоматически — если продажа удалена, проверьте и при необходимости оформите его.',
      unanswered:
        'Не удалось подтвердить удаление продажи: связь с сервером прервалась. Возможно, продажа уже удалена. Откройте продажу и проверьте её, прежде чем удалять снова: если она удалена, чек возврата в ОФД мог не быть выбит — проверьте и при необходимости оформите его.',
      unattributable:
        'Продажа уже удалена, но приложение не может подтвердить, что удалило её именно это действие. Чек возврата в ОФД не выбит автоматически — проверьте продажу и при необходимости оформите возврат.',
    },
    default: {
      unrecognized: 'Сервер ответил непредвиденно: приложение не может подтвердить результат операции. Проверьте его, прежде чем повторять.',
      unanswered:
        'Не удалось подтвердить операцию: связь с сервером прервалась. Возможно, она уже выполнена — проверьте результат, прежде чем повторять.',
      unattributable: 'Операция уже выполнена, но приложение не может подтвердить, что выполнило её именно это действие. Проверьте результат.',
    },
  },
  // Review 1, S3-5: the append's outcome is unknown; a repeat could book the operation twice.
  maybeQueued:
    'Не удалось подтвердить сохранение операции на этом устройстве. Возможно, она сохранена и будет отправлена. НЕ повторяйте её: проверьте список операций через минуту.',
} as const

/** How long a seam notice stays up. */
export const WRITE_W_NOTICE_MS = 8_000
/** The same notice is not repeated within this window (a till may refuse several writes in a row). */
export const WRITE_W_NOTICE_REPEAT_MS = 10_000

/** The text of a seam notice. */
export const writeWNoticeText = (notice: WriteWNotice): { message: string; type: AppToastType } => {
  if (notice.kind === 'refused') return { message: WRITE_W_NOTICE_TEXT.refused[notice.reason], type: 'danger' }
  if (notice.kind === 'syncing') return { message: WRITE_W_NOTICE_TEXT.syncing, type: 'warning' }
  if (notice.kind === 'outcome-unknown') {
    const copy = notice.label === 'sales.delete' ? WRITE_W_NOTICE_TEXT.outcomeUnknown['sales.delete'] : WRITE_W_NOTICE_TEXT.outcomeUnknown.default
    return { message: copy[notice.cause], type: 'danger' }
  }
  if (notice.kind === 'maybe-queued') return { message: WRITE_W_NOTICE_TEXT.maybeQueued, type: 'danger' }
  if (notice.kind === 'preview-unavailable') return { message: WRITE_W_NOTICE_TEXT.previewUnavailable, type: 'warning' }
  const text = (WRITE_W_NOTICE_TEXT.notSent as Record<string, string>)[notice.why] ?? WRITE_W_NOTICE_TEXT.notSent['no-drainer']
  return { message: text, type: 'danger' }
}

/**
 * How long a seam notice stays up: the ones that ask the cashier to CHECK something before acting again
 * (an outcome that may have applied, a fiscal return that may be owed, a write that must not be
 * repeated) stay as long as the persistent notices; the rest are the short ones.
 */
export const writeWNoticeDuration = (notice: WriteWNotice): number =>
  notice.kind === 'outcome-unknown' || notice.kind === 'maybe-queued' || (notice.kind === 'not-sent' && notice.why === 'queue-held')
    ? WRITE_CUTOVER_NOTICE_MS
    : WRITE_W_NOTICE_MS

/** A notice sink that shows each text at most once per `WRITE_W_NOTICE_REPEAT_MS`. */
export const writeWNoticeToaster = (
  show: (message: string, type: AppToastType, durationMs: number) => void,
  now: () => number = Date.now,
): ((notice: WriteWNotice) => void) => {
  const lastShown = new Map<string, number>()
  return (notice) => {
    const { message, type } = writeWNoticeText(notice)
    const t = now()
    const last = lastShown.get(message)
    if (last !== undefined && t - last < WRITE_W_NOTICE_REPEAT_MS) return
    lastShown.set(message, t)
    show(message, type, writeWNoticeDuration(notice))
  }
}

export interface SessionOutboxOptions {
  /** The watcher's `reevaluateWritePlane`, for the drainer's `master-changed` re-check. */
  onMasterRecheck?: () => void
  /** PostHog super-properties for the outbox sink. */
  register?: (props: Record<string, number | null>) => unknown
}

/**
 * The session's outbox dependencies. Built fresh on every call, deliberately: they are used only when
 * a drainer is CREATED (`ensureSessionOutboxV2` ignores them for a running one), so a cache would only
 * pin the first caller's `register` and keep one sink per organization for the life of the process.
 */
export const sessionOutboxDepsFor = (options: SessionOutboxOptions = {}): SessionOutboxV2Deps => ({
  ...productionSessionOutboxV2Deps(options.register),
  ...(options.onMasterRecheck ? { onMasterRecheck: options.onMasterRecheck } : {}),
})

/** The watcher's write plane, bound to the app. */
export const createAppWritePlane = ({
  organizationId,
  showToast,
  reload,
  onMasterRecheck,
  register,
  logger = console,
}: AppWritePlaneParams): OrgWritePlaneWatch<WriteCutoverDecision> => {
  const toast = (message: string, type: AppToastType, duration: number): void => {
    try {
      showToast(message, { type, duration })
    } catch (error) {
      logger.error('[write-cutover] a notice failed to display', { error })
    }
  }
  // One sink for this watch's pages; the drainer gets its own deps when (if) it is created.
  const sink = createOutboxAlertSink({ register })
  // P9 (S2's required "visible notice", §11): the seam's refusals and waits are shown at the till.
  // Nothing calls the sink in a Firestore-mode session: the seam notifies only on refuse and Supabase.
  installWriteWNotices(writeWNoticeToaster((message, type, durationMs) => toast(message, type, durationMs)))
  return createWritePlaneWatch({
    organizationId,
    logger,
    readSessionPin: getSessionWriteModePin,
    capability: WRITE_W_CAPABILITY,
    remember: (value) => writesMasterMemory.remember(organizationId, value, new Date()),
    startWriteCutover: () =>
      performWriteCutover({
        notify: () => toast(WRITE_CUTOVER_TEXT.writeCutover, 'warning', LIVE_ROLLBACK_GRACE_MS),
        reload,
        drainPendingSkips: () => drainPendingSkipsBeforeRestart(),
        awaitDrain: () => waitForWritesToDrain({ capMs: LIVE_ROLLBACK_DRAIN_CAP_MS }),
        logger,
        onPendingSkipsLost: (result) =>
          captureOperationalEvent('write_cutover_pending_skips_lost', {
            outcome: result.outcome,
            before: result.before,
            remaining: result.remaining,
            settled: result.settled,
            error: result.error,
            gate: result.gate,
            labels: Object.keys(result.remainingLabels),
          }),
      }),
    startV2DrainThenRestart: (decision, memory) =>
      performV2DrainThenRestart({
        action: decision.action,
        openOutbox: () => ensureSessionOutboxV2(organizationId, sessionOutboxDepsFor({ onMasterRecheck, register })),
        notifySyncing: () => toast(WRITE_CUTOVER_TEXT.syncing, 'warning', WRITE_CUTOVER_NOTICE_MS),
        notifyBlocked: (reason) =>
          toast(
            WRITE_CUTOVER_TEXT.blocked[reason] + (memory.lowersMemory() ? WRITE_CUTOVER_TEXT.untilResolved : ''),
            'danger',
            WRITE_CUTOVER_NOTICE_MS,
          ),
        notifyRestart: () => toast(WRITE_CUTOVER_TEXT.restartAfterSync, 'warning', LIVE_ROLLBACK_GRACE_MS),
        reload,
        page: (alert) => sink.rollbackBlocked(alert),
        rememberDrained: memory.rememberDrained,
        rememberUndrained: memory.rememberUndrained,
        awaitDrain: () => waitForWritesToDrain({ capMs: LIVE_ROLLBACK_DRAIN_CAP_MS }),
        // Review 1, item 1: a drain that lowers nothing (row 3) is capped, so the kill switch fires.
        capMs: ROW3_DRAIN_CAP_MS,
        capApplies: () => !memory.lowersMemory(),
        notifyCappedRestart: () => toast(WRITE_CUTOVER_TEXT.restartUndrained, 'warning', LIVE_ROLLBACK_GRACE_MS),
        logger,
      }),
    report: (event) => {
      if (event.type === 'transition-started') {
        captureOperationalEvent('write_cutover_live_transition', {
          action: event.decision.action,
          sequence: event.decision.sequence,
          from_reads_supabase: event.decision.from.readsSupabase,
          from_write_mode: event.decision.from.writeMode,
          to_reads_supabase: event.decision.to.readsSupabase,
          to_write_mode: event.decision.to.writeMode,
          to_write_mode_reason: event.decision.to.reason,
          alerts: [...event.decision.alerts],
        })
      } else if (event.type === 'transition-retargeted') {
        captureOperationalEvent('write_cutover_live_transition_retargeted', {
          action: event.decision.action,
          remember: event.remember,
        })
      } else if (event.type === 'transition-cancelled') {
        captureOperationalEvent('write_cutover_live_transition_cancelled', { key: event.key, why: event.why })
      } else {
        captureOperationalEvent('write_cutover_org_flags_alert', { alerts: [...event.alerts] })
      }
    },
  })
}

/** Organizations whose unreadable startup look was already paged in this process (review 2, item 3). */
const startupLookPaged = new Set<string>()

/** Test seam: a new process. */
export const resetStartupLookPagingForTests = (): void => {
  startupLookPaged.clear()
}

// ============================================================================
// P8 — the v1 → v2 migration at the flip, and the 409 conversion's follow-up
// ============================================================================

/** The options of the latest `startSessionOutboxAfterPin` (the watcher hook and the PostHog sink). */
let lastSessionOutboxOptions: {
  organizationId: string
  options: SessionOutboxOptions & { deps?: SessionOutboxV2Deps }
} | null = null

/** Organizations whose refused mirrors were converted into v2 before the session was pinned. */
const convertedBeforePinOrgs = new Set<string>()

/** One migration per organization at a time in this JS context. */
const migrationsInFlight = new Map<string, Promise<V1MigrationResult | null>>()

/** Test seam: a new process. */
export const resetV1MigrationSessionForTests = (): void => {
  lastSessionOutboxOptions = null
  convertedBeforePinOrgs.clear()
  migrationsInFlight.clear()
}

/** What `kickV1MigrationAfterPin` needs from `v1Migration.ts` (required lazily: never loaded for an organization today). */
interface V1MigrationModule {
  migrateV1EntriesToV2: (orgFirestoreId: string, deps: V1MigrationDeps) => Promise<V1MigrationResult>
  productionV1MigrationDeps: () => V1MigrationDeps
}

/**
 * Start (or kick) this organization's session drainer because its v2 queue holds entries the session
 * did not queue itself (P8: migrated or converted). Only for the pinned organization; never throws.
 */
const ensureDrainerForConvertedEntries = (
  organizationId: string,
  options: SessionOutboxOptions & { deps?: SessionOutboxV2Deps },
): boolean => {
  try {
    const pin = getSessionWriteModePin()
    if (!pin || pin.decision.organizationId !== organizationId) return false
    if (getSessionOutboxV2(organizationId)) {
      // A running drainer was started with its local view (P6), so there is nothing to activate.
      triggerActiveSessionOutboxV2('enqueue')
      return true
    }
    const deps = options.deps ?? sessionOutboxDepsFor(options)
    // P6's invariant, kept: a session's drainer and its local view start together, the view FIRST, so
    // every delivery is an acknowledgement it hears. No startup listing here (it was consumed by the
    // pin's own call): the view lists the queue itself and then settles the startup barrier.
    try {
      activateLocalView(organizationId, { readsSupabase: pin.decision.sessionReadsSupabase, listing: null, openStore: deps.openStore })
    } catch (error) {
      console.error('[localView] the local view could not start; the drainer starts regardless', { organizationId, error })
    }
    ensureSessionOutboxV2(organizationId, deps)
    return true
  } catch (error) {
    console.error('[outboxV2] the session drainer could not start for migrated / converted entries', { organizationId, error })
    return false
  }
}

/**
 * The running bundle's identity for an outbox entry (`bundleId`): the EAS update id, else the web build.
 * Informational (no rule reads it); null when it cannot be read. Required lazily: the provenance module
 * loads `expo-updates`, which this session module must not need at load time.
 */
export const currentBundleId = (): string | null => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { runningBundleProperties } = require('../analytics/writeBundleCapability') as {
      runningBundleProperties: () => { update_id?: unknown; web_build_at?: unknown }
    }
    const bundle = runningBundleProperties()
    if (typeof bundle.update_id === 'string' && bundle.update_id.length > 0) return bundle.update_id
    if (typeof bundle.web_build_at === 'string' && bundle.web_build_at.length > 0) return `web:${bundle.web_build_at}`
    return null
  } catch {
    return null
  }
}

/**
 * P9 — the backend-first branch's production dependencies (`writeWBackendFirst.ts`): the session's
 * store and alert sink, its drainer (kicked after every append — started, with its local view, if the
 * session has none), the drainer's exclusive send (invariant 12) and status, and the seam's notices.
 */
export const backendFirstDepsFor = (
  deps: SessionOutboxV2Deps,
  options: SessionOutboxOptions & { deps?: SessionOutboxV2Deps } = {},
): BackendFirstDeps => ({
  openStore: deps.openStore,
  sink: deps.sink,
  kickDrainer: (organizationId) => ensureDrainerForConvertedEntries(organizationId, { ...options, deps }),
  sendExclusive: (organizationId) => sessionOutboxV2SendExclusive(organizationId),
  subscribeQueue: (organizationId, listener) => {
    const drainer = getSessionOutboxV2(organizationId)
    if (!drainer) return null
    const unsubscribe = drainer.subscribe(listener)
    listener(drainer.status())
    return unsubscribe
  },
  bundleId: currentBundleId,
  now: () => Date.now(),
  notice: notifyWriteW,
})

/**
 * Start the v1 → v2 migration for a Supabase-master session (`v1Migration.ts`; design §2, §16). Called in
 * the callback that pinned the session, and REGISTERED SYNCHRONOUSLY (`beginV1Migration`) before it
 * returns, so every live v2 append of this session waits for it (`v1MigrationBarrier.ts`): that is what
 * puts the migrated writes at the head. Re-run on every Supabase-master pin (it is idempotent, and a stray
 * Firestore-first write may have reached v1 since); one at a time per organization. When it moved
 * anything, the session's drainer is started so it is delivered. Never rejects.
 */
/**
 * Review 2, N-a — when a running migration is paged as stuck.
 *
 * Acquiring its locks is bounded: `V1_EXCLUSIVE_WAIT_MS` (90 s) for the v2 drain Web Lock and the v1
 * queue together, then `busy`. The copy after that is NOT bounded, on purpose: it is at most 100 appends
 * (v1's `MAX_ENTRIES`) of a few ms each on healthy storage, plus one v1 save, and an append in flight
 * cannot be abandoned safely (P2: a failed or interrupted append is only proven absent by the store
 * itself). So nothing aborts it — but the session's drainer, `enqueuePrepared` and v1 enqueues all wait
 * on it, so a person is told: 90 s for the locks + 60 s for the copy on a slow device = 150 s.
 */
export const V1_MIGRATION_WATCHDOG_MS = 150_000

/** The migration has not ended after `V1_MIGRATION_WATCHDOG_MS` (paged once per run; never aborted). */
export const reportStuckV1Migration = (organizationId: string, elapsedMs: number): void => {
  console.error('[outboxV2] the v1 → v2 migration has not ended; the session drainer and live v2 writes are waiting on it', {
    organizationId,
    elapsedMs,
  })
  Sentry.captureException(new Error('outbox v2: the v1 → v2 migration has not ended'), {
    level: 'error',
    fingerprint: ['outbox-v1-migration-stuck'],
    tags: { outbox_v1_migration_stuck: true },
    contexts: { outbox_v2: { organizationId, elapsedMs } },
  })
  captureOperationalEvent('outbox_v1_migration_stuck', { elapsed_ms: elapsedMs })
}

export const kickV1MigrationAfterPin = (
  organizationId: string,
  options: SessionOutboxOptions & {
    deps?: SessionOutboxV2Deps
    migrationDeps?: V1MigrationDeps
    /** Injected for tests: the watchdog's delay and what it calls. */
    watchdog?: { ms?: number; page?: (organizationId: string, elapsedMs: number) => void }
  } = {},
): Promise<V1MigrationResult | null> => {
  const running = migrationsInFlight.get(organizationId)
  if (running) return running
  const end = beginV1Migration(organizationId)
  const startedAt = Date.now()
  const watchdogMs = options.watchdog?.ms ?? V1_MIGRATION_WATCHDOG_MS
  const page = options.watchdog?.page ?? reportStuckV1Migration
  const watchdog = setTimeout(() => {
    try {
      page(organizationId, Date.now() - startedAt)
    } catch {
      // A page that fails never touches the migration.
    }
  }, watchdogMs)
  const run = (async (): Promise<V1MigrationResult | null> => {
    // Yield once: the `finally` below must never run before `migrationsInFlight.set` (a synchronous
    // failure would otherwise leave a settled run registered as "in flight" for good).
    await Promise.resolve()
    let result: V1MigrationResult | null = null
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const migration = require('./v1Migration') as V1MigrationModule
      result = await migration.migrateV1EntriesToV2(organizationId, options.migrationDeps ?? migration.productionV1MigrationDeps())
    } catch (error) {
      console.error('[outboxV2] the v1 → v2 migration failed to run', { organizationId, error })
    } finally {
      // The barrier lifts and the slot frees BEFORE the drainer starts: the drainer must see a queue
      // no migration is still appending to.
      clearTimeout(watchdog)
      end()
      migrationsInFlight.delete(organizationId)
    }
    if (result && result.moved + result.alreadyInV2 > 0) ensureDrainerForConvertedEntries(organizationId, options)
    return result
  })()
  migrationsInFlight.set(organizationId, run)
  return run
}

/**
 * The v1 drain or a live write converted refused mirrors of this organization into v2 (409
 * `WRITE_MASTER_SUPABASE`, `mirrorRefusalConversion.ts`): the backend says the organization is
 * Supabase-master while this session may not know it. Through P5's own path — no new mechanism:
 *  - deliver: the session's v2 drainer is started (or kicked, `enqueue`), the way
 *    `startSessionOutboxAfterPin` starts it for a queue that holds entries; before the pin, it is
 *    remembered and started by the pin's own call;
 *  - re-read: the watcher's `reevaluateWritePlane`, which reloads the session into its new write mode
 *    once the flag is server-confirmed (stage-3 §4/§14), exactly as the drainer's `master-changed`
 *    re-check does.
 * Never throws.
 */
export const onMirrorsConvertedToMaster = (organizationId: string): void => {
  try {
    const pin = getSessionWriteModePin()
    if (!pin || pin.decision.organizationId !== organizationId) {
      convertedBeforePinOrgs.add(organizationId)
      return
    }
    const options = lastSessionOutboxOptions?.organizationId === organizationId ? lastSessionOutboxOptions.options : {}
    ensureDrainerForConvertedEntries(organizationId, options)
    options.onMasterRecheck?.()
  } catch (error) {
    console.error('[outboxV2] after a 409 conversion, delivery / the flag re-read could not be started', { organizationId, error })
  }
}

/**
 * After the startup pin (P5): a session whose queue holds entries — or that pinned Supabase mode
 * (P9) — gets its drainer now, so what an earlier session queued is delivered. Never throws.
 */
export const startSessionOutboxAfterPin = (
  organizationId: string,
  outboxV2: OutboxV2Presence,
  options: SessionOutboxOptions & {
    /** Injected for tests; the app builds the production dependencies. */
    deps?: SessionOutboxV2Deps
  } = {},
): boolean => {
  // P6: consumed on every call, so a listing can never outlive the startup look it came from.
  let probedListing: ReturnType<typeof takeProbedOutboxListing> = null
  try {
    probedListing = takeProbedOutboxListing(organizationId)
  } catch (error) {
    console.error('[localView] the startup listing could not be read back', { organizationId, error })
  }
  // P6: no drainer, so no local view to rebuild — release a startup barrier the look opened. Only after
  // the look itself (a `not-checked` call precedes a Supabase-master session's after-pin look).
  const notStarted = (): false => {
    if (outboxV2 !== 'not-checked') settleLocalViewStartupBarrier(organizationId)
    return false
  }
  let pin: ReturnType<typeof getSessionWriteModePin>
  let deps: SessionOutboxV2Deps
  try {
    pin = getSessionWriteModePin()
    if (!pin || pin.decision.organizationId !== organizationId) return notStarted()
    // P8: what the 409 conversion's follow-up reuses (`onMirrorsConvertedToMaster`). An assignment only.
    lastSessionOutboxOptions = {
      organizationId,
      options: { onMasterRecheck: options.onMasterRecheck, register: options.register, ...(options.deps ? { deps: options.deps } : {}) },
    }
  } catch (error) {
    console.error('[outboxV2] the session outbox could not be prepared', { organizationId, error })
    return notStarted()
  }
  // P8: a Supabase-master organization's v1 entries move into v2 now, in the callback that pinned the
  // session (`kickV1MigrationAfterPin`: registered synchronously, so it is ahead of every live write, and
  // the drainer below waits for it before its first pass). Never for an organization today: none
  // resolves to `'supabase'`.
  if (pin.decision.resolution.writesMaster === 'supabase') {
    try {
      void kickV1MigrationAfterPin(organizationId, options)
    } catch (error) {
      console.error('[outboxV2] the v1 → v2 migration could not be started', { organizationId, error })
    }
  }
  // P8: the v1 drain converted refused mirrors into v2 before this session was pinned.
  const convertedBeforePin = convertedBeforePinOrgs.delete(organizationId)
  try {
    if (outboxV2 !== 'non-empty' && outboxV2 !== 'unknown' && pin.decision.mode !== 'supabase' && !convertedBeforePin) return notStarted()
    deps = options.deps ?? sessionOutboxDepsFor(options)
  } catch (error) {
    console.error('[outboxV2] the session outbox could not be prepared', { organizationId, error })
    return notStarted()
  }
  // P6: the local view of this session's queue — exactly the sessions that get a drainer, i.e. whose
  // outbox v2 can hold entries. Activated BEFORE the drainer's first pass, so a delivery it makes is an
  // acknowledgement the view hears; rebuilt from the startup probe's listing when there was one, else
  // listed (the first catalogue write waits for it, bounded: `localViewStartupBarrier`). It never stops
  // delivery: a failure is logged and the drainer starts regardless.
  try {
    activateLocalView(organizationId, {
      readsSupabase: pin.decision.sessionReadsSupabase,
      listing: probedListing,
      openStore: deps.openStore,
    })
  } catch (error) {
    console.error('[localView] the local view could not start; the drainer starts regardless', { organizationId, error })
  }
  // Delivery and the page are independent: neither may stop the other (reviews 2 and 3, N2).
  let started = false
  try {
    ensureSessionOutboxV2(organizationId, deps)
    started = true
  } catch (error) {
    console.error('[outboxV2] the session drainer could not start', { organizationId, error })
  }
  // P9: a Supabase-mode session's W writes go through the backend-first branch. Installed even when
  // the drainer failed to start: a queued write is still persisted (and its write starts the drainer
  // again, `kickDrainer`), and a refuse-offline write is refused visibly rather than sent unordered.
  if (pin.decision.mode === 'supabase') {
    try {
      installWriteWBackendFirst(createBackendFirstBranch(backendFirstDepsFor(deps, options)))
    } catch (error) {
      console.error('[outboxV2] the backend-first branch could not be installed; W writes are refused', { organizationId, error })
    }
  }
  // A queue that could not be read is not known to be empty: the drainer is started (it retries the
  // store per pass) AND a person is told, because nothing else would say it (review 1, item 2) — once
  // per organization per process: `getInitialData` re-runs on every [db, user] change (review 2, item 3).
  if (outboxV2 === 'unknown' && !startupLookPaged.has(organizationId)) {
    startupLookPaged.add(organizationId)
    try {
      deps.sink.startupLookFailed({ mode: pin.decision.mode, writesMaster: pin.decision.resolution.writesMaster })
    } catch (error) {
      console.error('[outboxV2] the startup-look page failed', { organizationId, error })
    }
  }
  return started
}
